"use client";

import {
    type BrokingChargeBreakdownProps,
    type BrokingChargeData,
    type BrokingChargeRow,
    type BrokingChargeTab,
    type BrokingChargeTabId,
} from "@/app/interface/broking";
import { cn } from "@/lib/utils";
import { BarChart3, Package, TrendingUp } from "lucide-react";
import { useState } from "react";

const TABS: BrokingChargeTab[] = [
    { id: "equity", label: "Equity" },
    { id: "fno", label: "F&O" },
    { id: "commodity", label: "Commodity" },
];

const CHARGE_DATA: BrokingChargeData = {
    equity: {
        columns: ["Equity Delivery", "Equity Intraday"],
        rows: [
            {
                charge: "Brokerage",
                values: ["0.1% or ₹9/order (whichever is lower)", "0.01% or ₹9/order (lower)"],
            },
            {
                charge: "STT",
                values: ["0.1% on buy & sell", "0.025% on sell side"],
            },
            {
                charge: "Transaction Charges",
                values: ["₹307/crore", "₹307/crore"],
            },
            {
                charge: "GST",
                values: [
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                ],
            },
            {
                charge: "SEBI Charges",
                values: ["₹10/crore", "₹10/crore"],
            },
            {
                charge: "Stamp Charges",
                values: ["0.015% on buy side", "0.003% on buy side"],
            },
            /* {
                charge: "DP Charges (Sell)",
                values: ["₹15 + 18% GST per script", "—"],
            }, */
        ],
    },
    fno: {
        columns: ["Equity Delivery", "Equity Intraday"],
        rows: [
            { charge: "Brokerage", values: ["₹9/Order", "₹9/Order"] },
            {
                charge: "STT",
                values: ["0.05% on Sell Side", "0.15% on Sell Side (on Premium), 0.15% to buy"],
            },
            {
                charge: "Transaction Charges",
                values: ["₹225/crore", "₹4003/crore of premium"],
            },
            {
                charge: "GST",
                values: [
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                ],
            },
            { charge: "SEBI Charges", values: ["₹10/crore", "₹10/Crore"] },
            {
                charge: "Stamp Charges",
                values: ["0.002% on Buy-side", "0.003% on Buy-side"],
            },
        ],
    },
    commodity: {
        columns: ["Equity Delivery", "Equity Intraday"],
        rows: [
            { charge: "Brokerage", values: ["₹9/Order", "₹9/Order"] },
            {
                charge: "STT",
                values: [
                    "0.01% on Sell Side (Non-Agri)",
                    "0.05% on Sell Side (on Premium), 0.125% to buy",
                ],
            },
            {
                charge: "Transaction Charges",
                values: ["₹310/crore (Non-Agri)", "₹5180/crore of premium"],
            },
            {
                charge: "GST",
                values: [
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                    "18% on Brokerage+Transaction Charge+SEBI Charges",
                ],
            },
            { charge: "SEBI Charges", values: ["₹10/crore", "₹10/Crore"] },
            {
                charge: "Stamp Charges",
                values: ["0.002% on Buy-side", "0.003% on Buy-side"],
            },
        ],
    },
};

const TAB_ICONS: Record<BrokingChargeTabId, React.ReactNode> = {
    equity: <TrendingUp className="size-4 shrink-0 md:size-3.5" aria-hidden />,
    fno: <BarChart3 className="size-4 shrink-0 md:size-3.5" aria-hidden />,
    commodity: <Package className="size-4 shrink-0 md:size-3.5" aria-hidden />,
};

interface TabButtonProps {
    tab: BrokingChargeTab;
    isActive: boolean;
    onClick: (id: BrokingChargeTabId) => void;
    variant: "mobile" | "desktop";
}

function TabButton({ tab, isActive, onClick, variant }: TabButtonProps) {
    if (variant === "mobile") {
        return (
            <button
                type="button"
                onClick={() => onClick(tab.id)}
                className={cn("shrink-0", isActive && "rounded-t-[20px] bg-white p-1")}
            >
                <span
                    className={cn(
                        "flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full px-4 py-2.5 text-xs font-medium tracking-[-0.15px]",
                        isActive ? "bg-brand-primary-500 text-white" : "text-white",
                    )}
                >
                    {TAB_ICONS[tab.id]}
                    {tab.label}
                </span>
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={() => onClick(tab.id)}
            className={cn(
                "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border-none px-5 py-2 text-sm transition-all duration-200",
                isActive
                    ? "bg-gradient-to-br from-brand-primary-500 to-brand-primary-400 font-semibold text-white"
                    : "bg-transparent font-regular text-[#314158] hover:text-slate-700",
            )}
        >
            {TAB_ICONS[tab.id]}
            {tab.label}
        </button>
    );
}

interface TableRowProps {
    row: BrokingChargeRow;
    isLast: boolean;
}

function ChargeTableRow({ row, isLast }: TableRowProps) {
    return (
        <tr className={cn(!isLast && "border-b border-[rgba(14,15,23,0.1)] md:border-neutral-100")}>
            <td className="min-w-[115px] whitespace-nowrap px-2.5 py-3 align-top text-xs font-regular leading-[1.6] tracking-[0.12px] text-[#0F1016] md:min-w-[200px] md:px-5 md:py-4 md:text-base md:leading-[160%] md:tracking-normal md:text-neutral-500">
                {row.charge}
            </td>
            {row.values.map((value, index) => (
                <td
                    key={`${row.charge}-${index}`}
                    className={cn(
                        "min-w-[128px] whitespace-nowrap md:whitespace-normal px-2.5 py-3 align-top text-xs font-regular leading-[1.6] tracking-[0.12px] text-[#0F1016] md:min-w-0 md:px-5 md:py-4 md:text-base md:leading-[160%] md:tracking-normal md:text-neutral-500",
                        value === "—" && "text-neutral-400",
                    )}
                >
                    {value}
                </td>
            ))}
        </tr>
    );
}

export default function BrokingChargeBreakdown({
    data = CHARGE_DATA,
    tabs = TABS,
    title = "Detailed Charge Breakdown",
}: BrokingChargeBreakdownProps) {
    const [activeTab, setActiveTab] = useState<BrokingChargeTabId>(tabs[0]?.id ?? "equity");

    const current = data[activeTab];

    if (!current) {
        return null;
    }

    return (
        <div className="w-full">
            {/* Mobile tabs (Figma 504:1641) */}
            <div className="flex w-full items-end justify-between pt-3 md:hidden">
                {tabs.map((tab) => (
                    <TabButton
                        key={tab.id}
                        tab={tab}
                        isActive={activeTab === tab.id}
                        onClick={setActiveTab}
                        variant="mobile"
                    />
                ))}
            </div>

            {/* Desktop tabs */}
            <div className="rounded-b-0 hidden w-fit max-w-full gap-1 overflow-x-auto rounded-t-[10px] bg-white p-1.5 scrollbar-hide md:flex">
                {tabs.map((tab) => (
                    <TabButton
                        key={tab.id}
                        tab={tab}
                        isActive={activeTab === tab.id}
                        onClick={setActiveTab}
                        variant="desktop"
                    />
                ))}
            </div>

            <div className="overflow-hidden rounded-2xl rounded-t-none bg-white md:rounded-[14px] md:rounded-tl-none md:border">
                <div className="px-2.5 pb-2 pt-[18px] md:px-8 md:pb-0 md:pt-8">
                    <h3 className="text-lg font-semibold leading-[1.3] text-[#101828] md:mb-5 md:text-xl md:font-bold md:tracking-[-0.02em] md:text-neutral-900">
                        {title}
                    </h3>
                </div>

                <div className="-mx-0 overflow-x-auto overscroll-x-contain px-2.5 pb-4 scrollbar-hide md:px-8 md:pb-8">
                    <table className="w-full min-w-[368px] border-collapse md:min-w-[520px]">
                        <thead>
                            <tr className="bg-[#F4F5F9] md:bg-[#E9EAF3]">
                                <th className="min-w-[115px] whitespace-nowrap px-2.5 py-2.5 text-left text-sm font-medium leading-[1.6] tracking-[0.14px] text-[#0F1016] md:rounded-l-full md:px-5 md:py-3 md:text-base md:font-bold md:text-neutral-900">
                                    Charge
                                </th>
                                {current.columns.map((column, index) => (
                                    <th
                                        key={column}
                                        className={cn(
                                            "min-w-[128px] whitespace-nowrap px-2.5 py-2.5 text-left text-sm font-medium leading-[1.6] tracking-[0.14px] text-[#0F1016] md:px-5 md:py-3 md:text-base md:font-bold md:text-neutral-900",
                                            index === current.columns.length - 1 &&
                                                "md:rounded-r-full",
                                        )}
                                    >
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {current.rows.map((row, index) => (
                                <ChargeTableRow
                                    key={row.charge}
                                    row={row}
                                    isLast={index === current.rows.length - 1}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="-mb-3 mt-3 text-center text-sm italic text-gray-200 md:-mb-5 md:text-base">
                Charges may change if exchanges, SEBI, or the government revise rates.
            </p>
        </div>
    );
}
